import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Select,
  MenuItem,
  CardActions,
  TextField,
  Radio,
  ButtonBase,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// components
import Label from '../../components/Label';
// utils
import axios from '../../utils/axios';

const AI_USAGE_OPTIONS = [
  { label: "코딩 어시스턴트로 개발", caption: "Cursor, Copilot, Claude Code 등", value: "coding_assistant" },
  { label: "AI 기능을 제품에 직접 구현", caption: "LLM API, RAG, 에이전트 등", value: "build_ai_feature" },
  { label: "생성형 AI로 디자인·이미지·영상 제작", caption: "Midjourney, 생성형 AI 등", value: "generative_design" },
  { label: "AI로 콘텐츠·카피 작성", caption: "", value: "content_writing" },
  { label: "리서치·기획에 AI 활용", caption: "", value: "research_planning" },
  { label: "반복 업무 자동화", caption: "워크플로우, 봇, n8n 등", value: "automation" },
  { label: "기타", caption: "", value: "etc" },
];

const getAiUsageLabel = (value) => AI_USAGE_OPTIONS.find((option) => option.value === value)?.label || value;

const ENGLISH_OPTIONS = [
  { label: "업무 커뮤니케이션 가능", description: "내부 공유용 문서 작성, 메신저 소통 등", value: "communication" },
  { label: "외부 공개용 영어 콘텐츠 작성 가능", description: "블로그, 커머스 상세페이지, 아티클 등", value: "content" },
  { label: "현지 영어권 문화에 매우 익숙함", description: "영어권 국가에서 오랜 거주 경험", value: "culture" },
];

function getStyles(name, selection, theme) {
  return {
    fontWeight:
      selection.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

// ----------------------------------------------------------------------

export default function AdditionalSkillCard({ profile, skillOption=[], isApply=false, handleAfterApply }) {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [isSubmit, setIsSubmit] = useState(false);

  const showFullStackQuestion = user?.crew?.position === "developer";
  const showContentQuestion = user?.crew?.position === "designer" || user?.crew?.position === "marketer";

  // values
  const [fullstackExperience, setFullstackExperience] = useState('');
  const [aiUsage, setAiUsage] = useState([]);
  const [aiExperience, setAiExperience] = useState('');
  const [contentExperience, setContentExperience] = useState('');
  const [english, setEnglish] = useState('');
  const [englishExperience, setEnglishExperience] = useState('');
  const [moreSkills, setMoreSkills] = useState([]);

  const handleChangeAiUsage = (event) => {
    const { target: { value }, } = event;
    // On autofill we get a stringified value.
    const nextValue = (value && typeof value === 'string') ? value.split(',') : value;
    setAiUsage(nextValue);
    if (!nextValue || nextValue.length === 0) setAiExperience('');
  };

  const updateValues = (newStatus) => {
    setFullstackExperience(newStatus.full_stack_experience || '');
    setAiUsage((newStatus.ai_usage && typeof newStatus.ai_usage === 'string') ? newStatus.ai_usage.split(',') : newStatus.ai_usage || []);
    setAiExperience(newStatus.ai_experience || '');
    setContentExperience(newStatus.content_experience || '');
    setEnglish(newStatus.english_proficiency || '');
    setEnglishExperience(newStatus.english_experience || '');
    setMoreSkills(newStatus.skills || []);
  }

  useEffect(() => {
    if (profile) {
      updateValues(profile);
    }
  }, [profile]);

  const sendSkill = async () => {
    try {
      setIsSubmit(true);
      const sendResponse = await axios.post('api/crews/profile/skill/', {
        full_stack_experience: fullstackExperience,
        ai_usage: (aiUsage && typeof aiUsage === 'object') ? aiUsage.join(',') : aiUsage,
        ai_experience: aiExperience,
        content_experience: contentExperience,
        english_proficiency: english,
        english_experience: englishExperience,
        skills: moreSkills,
      });
      updateValues(sendResponse.data);
      setIsSubmit(false);
      enqueueSnackbar('추가 스킬이 업데이트 되었습니다.', { variant: 'lightning' });
      if (isApply) handleAfterApply();
    } catch (e) {
      console.error(e);
      setIsSubmit(false);
      if (e.response && e.response.status === 400) {
        enqueueSnackbar(e.response.data.message, { variant: 'warning' });
      }
    }
  }

  return (
    <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>추가 스킬</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          추가 스킬을 설정하면 매칭 확률이 높아집니다.
        </Typography>

        {showFullStackQuestion && (
        <Stack sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            풀스택으로 개발하실 수 있다면,{!smUp && <br />} 풀스택 개발 경험을 간단히 알려주세요.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={fullstackExperience}
            onChange={(e) => e.target.value.length <= 1000 && setFullstackExperience(e.target.value)}
            placeholder="활용 가능한 스택, 어떤 서비스를 개발했는지 등"
            helperText={`${fullstackExperience.length.toLocaleString()}/1,000자`}
            sx={{ '& .MuiFormHelperText-root': { textAlign: 'right' } }}
          />
        </Stack>
        )}

        <Stack sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            업무에 AI를 어떻게 활용하고 계신가요?
          </Typography>
          <Select
            fullWidth
            multiple
            displayEmpty
            value={aiUsage}
            inputProps={{ sx: { py: 1.5 } }}
            onChange={handleChangeAiUsage}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <Typography sx={{ py: 0.5, color: "text.disabled" }}>해당하는 활용 방식을 모두 선택해주세요</Typography>;
              }
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={getAiUsageLabel(value)} />
                  ))}
                </Box>
              )
            }}
          >
            {AI_USAGE_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 0.75,
                  typography: 'body2',
                }}
                style={getStyles(option.value, aiUsage, theme)}
              >
                <Stack>
                  <Typography variant="body2">{option.label}</Typography>
                  {option.caption && (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {option.caption}
                    </Typography>
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </Stack>

        {aiUsage.length > 0 && (
        <Stack sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            구체적으로 어떤 도구로 무엇을 했는지 알려주세요.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={aiExperience}
            onChange={(e) => e.target.value.length <= 1000 && setAiExperience(e.target.value)}
            placeholder="예. Cursor로 OO 서비스 개발 / LLM API로 상담 챗봇 구현"
            helperText={`${aiExperience.length.toLocaleString()}/1,000자`}
            sx={{ '& .MuiFormHelperText-root': { textAlign: 'right' } }}
          />
        </Stack>
        )}

        {showContentQuestion && (
        <Stack sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            릴스, 쇼츠 등 숏폼 영상을 직접 기획하거나 제작하실 수 있다면, 어떤 콘텐츠를 제작해보셨는지 알려주세요.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={contentExperience}
            onChange={(e) => e.target.value.length <= 1000 && setContentExperience(e.target.value)}
            placeholder="공개 가능한 콘텐츠가 있다면 링크도 작성해주세요"
            helperText={`${contentExperience.length.toLocaleString()}/1,000자`}
            sx={{ '& .MuiFormHelperText-root': { textAlign: 'right' } }}
          />
        </Stack>
        )}

        <Stack sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            영어 사용에 능숙하신가요?
          </Typography>
          <Stack spacing={1}>
            {ENGLISH_OPTIONS.map((option, index) => (
              <ButtonBase
                key={index}
                sx={{
                  p: 1.5,
                  justifyContent: "flex-start",
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: (theme) => theme.palette.grey[500_24],
                  ": hover": { cursor: "pointer", borderColor: "primary.main", bgcolor: "grey.100" },
                  ...(english === option.value) && {
                    borderColor: "grey.600",
                    bgcolor: "grey.200"
                  }
                }}
                onClick={() => { if(english === option.value) setEnglish(''); else setEnglish(option.value)} }
              >
                <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                  <Radio color="primary" checked={english === option.value} />
                  <Stack alignItems="flex-start">
                    <Typography
                      variant={english === option.value ? "subtitle2" : "body2"}
                      sx={{
                        color: !english ? "text.primary" : english === option.value ? "text.primary" : "text.disabled",
                      }}
                    >
                      {option.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: !english ? "text.secondary" : english === option.value ? "text.primary" : "text.disabled" }}
                    >
                      {option.description}
                    </Typography>
                  </Stack>
                </Stack>
              </ButtonBase>
            ))}
          </Stack>
          {(english === "content" || english === "culture") && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "grey.100",
                borderRadius: 2,
              }}
            >
              <Label variant="filled">
                추가 질문
              </Label>
              <Typography variant="subtitle2" sx={{ my: 1 }}>
                {english === "content" ? "영어 콘텐츠를 작성한 경험을 알려주세요." : "영어권 문화에 익숙하신 이유를 알려주세요." }
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={englishExperience}
                onChange={(e) => e.target.value.length <= 1000 && setEnglishExperience(e.target.value)}
                placeholder={english === "content" ? "예. 글로벌 기업 OO의 블로그 글 N개 작성" : "예. 미국에서 대학교 졸업 / 미국 9년 거주"}
                helperText={`${englishExperience.length.toLocaleString()}/1,000자`}
                sx={{ '& .MuiFormHelperText-root': { textAlign: 'right' } }}
              />
            </Box>
          )}              
        </Stack>

        <Stack sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            추가로 활용 가능한 기술
          </Typography>
          <Autocomplete
            multiple
            fullWidth
            value={moreSkills}
            onChange={(_, values) => setMoreSkills(values)}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            options={skillOption}
            getOptionLabel={(option) => option.name}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField 
                {...params}
                fullWidth
                label=""
                placeholder={"찾기"}
              />
            )}
          />
        </Stack>
      </CardContent>
      {!isApply && (
        <CardActions sx={{ px: 3, pb: 3, justifyContent: "flex-end" }}>
          <LoadingButton
            size="large"
            variant="contained"
            fullWidth={!smUp}
            onClick={sendSkill}
            loading={isSubmit}
          >
            추가 스킬 저장하기
          </LoadingButton>
        </CardActions>
      )}
      {isApply && (
        <CardActions
          sx={{
            left: 0, bottom: 0, width: 1, px: 2, pb: 3, justifyContent: "flex-end",
            bgcolor: "background.default",
            ...!smUp && {
              position: "fixed",
            }
          }}
        >
          <LoadingButton
            size="large"
            variant={(fullstackExperience === '' && aiUsage.length === 0 && contentExperience === '' && english === '' && moreSkills.length === 0) ? "outlined" : "contained"}
            fullWidth={!smUp}
            onClick={sendSkill}
            loading={isSubmit}
          >
            {(fullstackExperience === '' && aiUsage.length === 0 && contentExperience === '' && english === '' && moreSkills.length === 0) ? "건너뛰기" : "제출하기"}
          </LoadingButton>
        </CardActions>
      )}
    </Card>
  )
}